<template>
<BaseTemplate app-name="RIS System" page-title="">
  <template #content>
    <div
      class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200"
    >
      <!-- Main Content -->
      <div class="flex-1 flex overflow-hidden">
        <!-- Content Area -->
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- Header -->
          <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <div class="flex flex-wrap justify-between items-center gap-4">
              <div class="flex items-center gap-3">
                <h2 class="text-xl font-medium" :style="{ color: 'var(--clinic-font-color)' }">{{ currentView === 'list' ? 'Agendas Cadastradas' : 'Cadastro de Agenda' }}</h2>
                <button
                  v-if="currentView === 'form'"
                  class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  @click="goToList"
                  aria-label="Voltar para lista"
                >
                  <ArrowLeftIcon class="w-5 h-5" />
                </button>
              </div>
              <div class="flex items-center gap-2">
                <button
                  v-if="currentView === 'list'"
                  class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                  @click="openNewScheduleForm"
                >
                  <PlusIcon class="w-4 h-4" />
                  <span>Nova Agenda</span>
                </button>
                <button
                  v-if="currentView === 'form'"
                  class="clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                  @click="saveSchedule"
                  :disabled="!isFormValid || isSaving"
                >
                  <SaveIcon class="w-4 h-4" />
                  <span>{{ isSaving ? 'Salvando...' : (editMode ? 'Atualizar' : 'Salvar') }} Agenda</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Schedule List View -->
          <div v-if="currentView === 'list'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700 p-4">
              <!-- Search and Filter -->
              <div class="mb-4 flex flex-wrap gap-4 items-center">
                <div class="flex-1 min-w-[240px]">
                  <div class="relative">
                    <input
                      type="text"
                      v-model="searchQuery"
                      placeholder="Buscar agendas..."
                      @input="debouncedSearch"
                      class="h-10 w-full px-3 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div class="relative">
                  <select
                    v-model="filterStatus"
                    @change="loadSchedules(1)"
                    class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-blue-500 appearance-none pr-8"
                  >
                    <option value="">Todos os status</option>
                    <option value="true">Ativas</option>
                    <option value="false">Inativas</option>
                  </select>
                  <ChevronDownIcon class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>

                <div class="relative">
                  <select
                    v-model="filterType"
                    @change="loadSchedules(1)"
                    class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:ring-2 focus:ring-blue-500 appearance-none pr-8"
                  >
                    <option value="">Todos os tipos</option>
                    <option value="true">Consulta</option>
                    <option value="false">Procedimento</option>
                  </select>
                  <ChevronDownIcon class="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>

                <button
                  @click="resetFilters"
                  class="h-10 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500"
                >
                  <RefreshCwIcon class="w-4 h-4 inline-block" />
                </button>
              </div>

              <!-- Error Message -->
              <div v-if="errorMessage" class="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
                <p class="text-red-800 dark:text-red-400 text-sm">{{ errorMessage }}</p>
              </div>

              <!-- Warning Message - Agendas sem Serviços -->
              <div v-if="schedulesWithoutServices.length > 0" class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                <div class="flex items-start">
                  <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Atenção: Agendas sem Serviços Vinculados
                    </h3>
                    <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                      <p class="mb-2">
                        As seguintes agendas não possuem serviços vinculados e podem ter problemas:
                      </p>
                      <ul class="list-disc list-inside space-y-1 text-xs">
                        <li v-for="schedule in schedulesWithoutServices" :key="schedule.id">
                          <strong>{{ schedule.name }}</strong> - ID: {{ schedule.id }}
                        </li>
                      </ul>
                      <p class="mt-2 font-medium">
                        Recomendamos editar essas agendas e vincular pelo menos um serviço.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Loading Overlay -->
              <div v-if="isLoading" class="py-10 flex justify-center items-center">
                <LoaderIcon class="w-12 h-12 animate-spin text-gray-500 dark:text-gray-400" />
              </div>

              <!-- Schedule Table -->
              <div v-else class="overflow-x-auto">
                <table class="w-full border-collapse">
                  <thead>
                    <tr class="bg-gray-50 dark:bg-gray-900/50 text-left">
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Nome
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Tipo
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Médico
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Status
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Worklist
                      </th>
                      <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="schedules.length === 0">
                      <td colspan="6" class="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                        Nenhuma agenda encontrada
                      </td>
                    </tr>
                    <tr
                      v-for="schedule in schedules"
                      :key="schedule.id"
                      class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td class="px-4 py-3">
                        <div class="text-sm font-medium">{{ schedule.name }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">{{ schedule.allows_manual_registration ? 'Aceita cadastro manual' : 'Não aceita cadastro manual' }}</div>
                      </td>
                      <td class="px-4 py-3 text-sm">{{ schedule.is_consultation ? 'Consulta' : 'Procedimento' }}</td>
                      <td class="px-4 py-3 text-sm">{{ schedule.doctor?.name || 'Não definido' }}</td>
                      <td class="px-4 py-3 text-sm">
                        <span
                          class="px-2 py-1 text-xs rounded-full"
                          :class="{
                            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400': schedule.is_enabled,
                            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400': !schedule.is_enabled
                          }"
                        >
                          {{ schedule.is_enabled ? 'Ativa' : 'Inativa' }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-sm">
                        <span
                          class="px-2 py-1 text-xs rounded-full"
                          :class="{
                            'bg-emerald-100 dark:bg-emerald-900/30 text-blue-800 dark:text-blue-400': schedule.has_worklist_support,
                            'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400': !schedule.has_worklist_support
                          }"
                        >
                          {{ schedule.has_worklist_support ? 'Sim' : 'Não' }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-sm">
                        <div class="flex gap-2">
                          <button
                            @click="editSchedule(schedule)"
                            class="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                            title="Editar"
                          >
                            <EditIcon class="w-4 h-4" />
                          </button>
                          <button
                            @click="confirmDeleteSchedule(schedule)"
                            class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                            title="Excluir"
                          >
                            <TrashIcon class="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Pagination -->
                <div class="flex flex-col md:flex-row md:items-center md:justify-between my-4 gap-2">
                  <div class="flex items-center gap-2 justify-center">
                    <button
                      :disabled="currentPage === 1"
                      @click="loadSchedules(currentPage - 1)"
                      class="px-3 py-1 rounded bg-emerald-500 dark:bg-gray-700 text-gray-200 dark:text-gray-200 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <span class="text-sm text-gray-700 dark:text-gray-200">
                      Página {{ currentPage }} de {{ totalPages }}
                    </span>
                    <button
                      :disabled="currentPage === totalPages || totalPages === 0"
                      @click="loadSchedules(currentPage + 1)"
                      class="px-3 py-1 rounded bg-emerald-500 dark:bg-gray-700 text-gray-200 dark:text-gray-200 disabled:opacity-50"
                    >
                      Próxima
                    </button>
                  </div>
                  <div class="flex items-center gap-2 justify-center">
                    <label for="page-size" class="text-sm text-gray-700 dark:text-gray-200">Itens por página:</label>
                    <select
                      v-model.number="pageSize"
                      @change="loadSchedules(1)"
                      class="rounded border px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
                    >
                      <option :value="5">5</option>
                      <option :value="10">10</option>
                      <option :value="20">20</option>
                      <option :value="50">50</option>
                    </select>
                    <span class="text-xs text-gray-500 dark:text-gray-400">Total: {{ totalCount }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Schedule Form View with Tabs -->
          <div v-if="currentView === 'form'" class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
              <!-- Tabs Navigation -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <div class="flex overflow-x-auto">
                  <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    @click="activeTab = tab.id"
                    class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none"
                    :class="{
                      'border-b-2': activeTab === tab.id,
                      'border-b-2': activeTab === tab.id
                    }"
                    :style="{
                      color: activeTab === tab.id ? 'var(--clinic-icon-color)' : 'var(--clinic-font-color)',
                      borderColor: activeTab === tab.id ? 'var(--clinic-icon-color)' : 'transparent'
                    }"
                  >
                    <div class="flex items-center gap-2">
                      <component :is="tab.icon" class="w-4 h-4" />
                      {{ tab.name }}
                    </div>
                  </button>
                </div>
              </div>

              <!-- Tab Content -->
              <div class="p-6">
                <!-- ABA: Informações Básicas -->
                <div v-if="activeTab === 'basic'" class="space-y-6">
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Informações Básicas</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div class="md:col-span-2">
                        <label for="name" class="block text-sm font-medium mb-1">Nome da Agenda*</label>
                        <input
                          id="name"
                          type="text"
                          v-model="currentSchedule.name"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label for="is_consultation" class="block text-sm font-medium mb-1">Tipo de Agenda*</label>
                        <select
                          id="is_consultation"
                          v-model="currentSchedule.is_consultation"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Consulta</option>
                          <option :value="false">Procedimento</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="doctor_id" class="block text-sm font-medium mb-1">Médico Responsável*</label>
                        <select
                          id="doctor_id"
                          v-model="currentSchedule.doctor_id"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option value="">Selecione um médico</option>
                          <option v-for="doctor in doctors" :key="doctor.id" :value="doctor.id">
                            {{ doctor.name }}
                          </option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="is_enabled" class="block text-sm font-medium mb-1">Status da Agenda*</label>
                        <select
                          id="is_enabled"
                          v-model="currentSchedule.is_enabled"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Ativa</option>
                          <option :value="false">Inativa</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="allows_manual_registration" class="block text-sm font-medium mb-1">Aceita Cadastro Manual*</label>
                        <select
                          id="allows_manual_registration"
                          v-model="currentSchedule.allows_manual_registration"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Sim</option>
                          <option :value="false">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="allows_special_slot" class="block text-sm font-medium mb-1">Aceita Horário Especial*</label>
                        <select
                          id="allows_special_slot"
                          v-model="currentSchedule.allows_special_slot"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Sim</option>
                          <option :value="false">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="allow_attach_after_report" class="block text-sm font-medium mb-1">Permitir anexar/escanear após laudo*</label>
                        <select
                          id="allow_attach_after_report"
                          v-model="currentSchedule.allow_attach_after_report"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Sim</option>
                          <option :value="false">Não</option>
                        </select>
                      </div>
                      
                      <div>
                        <label for="allow_attach_after_attendance" class="block text-sm font-medium mb-1">Permitir anexar/escanear após atendimento*</label>
                        <select
                          id="allow_attach_after_attendance"
                          v-model="currentSchedule.allow_attach_after_attendance"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Sim</option>
                          <option :value="false">Não</option>
                        </select>
                      </div>

                      <div>
                        <label for="allow_unconfirm" class="block text-sm font-medium mb-1">Permitir desconfirmar um agendamento*</label>
                        <select
                          id="allow_unconfirm"
                          v-model="currentSchedule.allow_unconfirm"
                          required
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                        >
                          <option :value="true">Sim</option>
                          <option :value="false">Não</option>
                        </select>
                      </div>


                    </div>
                  </div>
                </div>

                <!-- ABA: Worklist -->
                <div v-if="activeTab === 'worklist'" class="space-y-6">
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Configuração de Worklist</h3>
                    <div class="mb-4">
                      <label for="has_worklist_support" class="block text-sm font-medium mb-1">Suporte a Worklist*</label>
                      <select
                        id="has_worklist_support"
                        v-model="currentSchedule.has_worklist_support"
                        required
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                      >
                        <option :value="true">Sim</option>
                        <option :value="false">Não</option>
                      </select>
                    </div>
                    <!-- NOVO CAMPO: Quando gerar o worklist -->
                    <div v-if="currentSchedule.has_worklist_support" class="mb-4">
                      <label for="worklist_generation_mode" class="block text-sm font-medium mb-1">Quando gerar o Worklist?</label>
                      <select
                        id="worklist_generation_mode"
                        v-model="currentSchedule.worklist_generation_mode"
                        required
                        class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                      >
                        <option value="confirmation">Ao confirmar agendamento</option>
                        <option value="attendance">Ao atender no módulo de atendimento</option>
                      </select>
                    </div>
                    <div v-if="currentSchedule.has_worklist_support" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label for="ip_server" class="block text-sm font-medium mb-1">IP Server*</label>
                        <input
                          id="ip_server"
                          type="text"
                          v-model="currentSchedule.worklist_config.ip_server"
                          :required="currentSchedule.has_worklist_support"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label for="port" class="block text-sm font-medium mb-1">Porta*</label>
                        <input
                          id="port"
                          type="number"
                          v-model.number="currentSchedule.worklist_config.port"
                          :required="currentSchedule.has_worklist_support"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label for="aetitle" class="block text-sm font-medium mb-1">AE Title*</label>
                        <input
                          id="aetitle"
                          type="text"
                          v-model="currentSchedule.worklist_config.aetitle"
                          :required="currentSchedule.has_worklist_support"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: Horários -->
                <div v-if="activeTab === 'schedule'" class="space-y-6">
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Horários da Agenda</h3>
                    <div class="overflow-x-auto">
                      <table class="w-full border-collapse">
                        <thead>
                          <tr class="bg-gray-50 dark:bg-gray-900/50">
                            <th class="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Dia</th>
                            <th class="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Ativo</th>
                            <th class="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Início Manhã</th>
                            <th class="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Fim Manhã</th>
                            <th class="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Início Tarde</th>
                            <th class="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Fim Tarde</th>
                            <th class="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">Intervalo (min)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(dia, index) in diasSemana" :key="index" class="border-b border-gray-200 dark:border-gray-700">
                            <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{{ dia.nome }}</td>
                            <td class="px-4 py-3 text-center">
                              <input 
                                type="checkbox" 
                                v-model="currentSchedule.slots[index].is_active" 
                                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </td>
                            <td class="px-4 py-3">
                              <input 
                                type="time" 
                                v-model="currentSchedule.slots[index].morning_start" 
                                :disabled="!currentSchedule.slots[index].is_active"
                                class="w-full px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:dark:bg-gray-800 disabled:text-gray-400"
                              />
                            </td>
                            <td class="px-4 py-3">
                              <input 
                                type="time" 
                                v-model="currentSchedule.slots[index].morning_end" 
                                :disabled="!currentSchedule.slots[index].is_active"
                                class="w-full px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:dark:bg-gray-800 disabled:text-gray-400"
                              />
                            </td>
                            <td class="px-4 py-3">
                              <input 
                                type="time" 
                                v-model="currentSchedule.slots[index].afternoon_start" 
                                :disabled="!currentSchedule.slots[index].is_active"
                                class="w-full px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:dark:bg-gray-800 disabled:text-gray-400"
                              />
                            </td>
                            <td class="px-4 py-3">
                              <input 
                                type="time" 
                                v-model="currentSchedule.slots[index].afternoon_end" 
                                :disabled="!currentSchedule.slots[index].is_active"
                                class="w-full px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:dark:bg-gray-800 disabled:text-gray-400"
                              />
                            </td>
                            <td class="px-4 py-3">
                              <input 
                                type="number" 
                                v-model="currentSchedule.slots[index].interval_minutes" 
                                :disabled="!currentSchedule.slots[index].is_active"
                                min="5"
                                step="5"
                                class="w-full px-2 py-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:dark:bg-gray-800 disabled:text-gray-400"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- ABA: Permissões -->
                <div v-if="activeTab === 'permissions'" class="space-y-6">
                  <div>
                    <h3 class="text-lg font-medium mb-4 text-gray-900 dark:text-white">Usuários com Permissão</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div 
                        v-for="user in users" 
                        :key="user.id" 
                        class="flex items-center"
                      >
                        <label class="inline-flex items-center p-3 border rounded-md cursor-pointer w-full" 
                          :class="currentSchedule.permitted_user_ids.includes(user.id) ? 'bg-emerald-100 dark:bg-emerald-900/30 border-blue-300 dark:border-blue-700' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700'"
                        >
                          <input 
                            type="checkbox" 
                            :value="user.id" 
                            v-model="currentSchedule.permitted_user_ids" 
                            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                          />
                          <span class="text-gray-700 dark:text-gray-300">{{ getUserDisplayName(user) }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- ABA: Serviços -->
<div v-if="activeTab === 'services'" class="space-y-6">
  <h3 class="text-lg font-medium text-gray-900 dark:text-white">Vincular Serviços à Agenda</h3>

  <!-- Banner de Aviso - Agenda sem Serviços Vinculados -->
  <div v-if="currentSchedule.service_ids.length === 0" class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
    <div class="flex items-start">
      <div class="flex-shrink-0">
        <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
      </div>
      <div class="ml-3">
        <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
          Atenção: Agenda sem Serviços Vinculados
        </h3>
        <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
          <p class="mb-2">
            Esta agenda não possui nenhum serviço vinculado. Isso pode causar problemas:
          </p>
          <ul class="list-disc list-inside space-y-1 text-xs">
            <li>Não será possível criar agendamentos manuais</li>
            <li>A importação do SISREG falhará para serviços não encontrados</li>
            <li>Os usuários não conseguirão selecionar serviços na agenda</li>
          </ul>
          <p class="mt-2 font-medium">
            Recomendamos vincular pelo menos um serviço antes de salvar a agenda.
          </p>
        </div>
      </div>
    </div>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
    
    <div class="flex flex-col gap-2">
      <label for="service-search" class="text-sm font-medium text-gray-700 dark:text-gray-300">Serviços Disponíveis</label>
      <div class="relative">
        <input
          id="service-search"
          type="text"
          v-model="serviceSearchQuery"
          placeholder="Buscar serviço..."
          class="h-10 w-full px-3 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
      </div>
      <div class="border rounded-md bg-white dark:bg-gray-900 dark:border-gray-700 h-72 overflow-y-auto">
        <ul v-if="availableServices.length > 0">
          <li
            v-for="service in availableServices"
            :key="service.id"
            @click="toggleSelection('available', service.id)"
            class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
            :class="{ 'bg-emerald-100 dark:bg-emerald-900/50': selectedAvailable.includes(service.id) }"
          >
            {{ service.descricao }}
          </li>
        </ul>
        <div v-else class="text-center text-sm text-gray-500 p-4">
            Nenhum serviço disponível ou todos já foram vinculados.
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-2 self-center pt-8">
        <button @click="moveSelectedToRight" :disabled="selectedAvailable.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Mover selecionado para a direita">
            &gt;
        </button>
        <button @click="moveAllToRight" :disabled="availableServices.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Mover todos para a direita">
            &gt;&gt;
        </button>
        <button @click="moveSelectedToLeft" :disabled="selectedLinked.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Mover selecionado para a esquerda">
            &lt;
        </button>
        <button @click="moveAllToLeft" :disabled="linkedServices.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Mover todos para a esquerda">
            &lt;&lt;
        </button>
    </div>

    <div class="flex flex-col gap-2">
       <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Serviços Vinculados</label>
       <div class="border rounded-md bg-white dark:bg-gray-900 dark:border-gray-700 h-72 mt-[2.5rem] overflow-y-auto">
         <ul v-if="linkedServices.length > 0">
           <li
            v-for="service in linkedServices"
            :key="service.id"
            @click="toggleSelection('linked', service.id)"
            class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
            :class="{ 'bg-emerald-100 dark:bg-emerald-900/50': selectedLinked.includes(service.id) }"
           >
             {{ service.descricao }}
           </li>
         </ul>
         <div v-else class="text-center text-sm text-gray-500 p-4">
             Nenhum serviço vinculado a esta agenda.
         </div>
       </div>
    </div>

  </div>
</div>

                <!-- ABA: Salas -->
                <div v-if="activeTab === 'rooms'" class="space-y-6">
                  <h3 class="text-lg font-medium text-gray-900 dark:text-white">Vincular Salas à Agenda</h3>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    Associe salas que estarão disponíveis para esta agenda. As salas são gerenciadas na tela de "Gerenciamento de Salas".
                  </p>

                  <div class="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
                    
                    <div class="flex flex-col gap-2">
                      <label for="room-search" class="text-sm font-medium text-gray-700 dark:text-gray-300">Salas Disponíveis</label>
                      <div class="relative">
                        <input
                          id="room-search"
                          type="text"
                          v-model="roomSearchQuery"
                          placeholder="Buscar sala..."
                          class="h-10 w-full px-3 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      </div>
                      <div class="border rounded-md bg-white dark:bg-gray-900 dark:border-gray-700 h-72 overflow-y-auto">
                        <ul v-if="availableRooms.length > 0">
                          <li
                            v-for="room in availableRooms"
                            :key="room.id"
                            @click="toggleRoomSelection('available', room.id)"
                            class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                            :class="{ 'bg-emerald-100 dark:bg-emerald-900/50': selectedAvailableRooms.includes(room.id) }"
                          >
                            <div class="font-medium">{{ room.name }}</div>
                            <div class="text-xs text-gray-500">{{ room.type }}</div>
                          </li>
                        </ul>
                        <div v-else class="text-center text-sm text-gray-500 p-4">
                            Nenhuma sala disponível ou todas já foram vinculadas.
                        </div>
                      </div>
                    </div>

                    <div class="flex flex-col gap-2 self-center pt-8">
                        <button @click="moveSelectedRoomsToRight" :disabled="selectedAvailableRooms.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Mover selecionado para a direita">
                            &gt;
                        </button>
                        <button @click="moveAllRoomsToRight" :disabled="availableRooms.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Mover todos para a direita">
                            &gt;&gt;
                        </button>
                        <button @click="moveSelectedRoomsToLeft" :disabled="selectedLinkedRooms.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Mover selecionado para a esquerda">
                            &lt;
                        </button>
                        <button @click="moveAllRoomsToLeft" :disabled="linkedRooms.length === 0" class="p-2 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Mover todos para a esquerda">
                            &lt;&lt;
                        </button>
                    </div>

                    <div class="flex flex-col gap-2">
                       <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Salas Vinculadas</label>
                       <div class="border rounded-md bg-white dark:bg-gray-900 dark:border-gray-700 h-72 mt-[2.5rem] overflow-y-auto">
                         <ul v-if="linkedRooms.length > 0">
                           <li
                            v-for="room in linkedRooms"
                            :key="room.id"
                            @click="toggleRoomSelection('linked', room.id)"
                            class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                            :class="{ 'bg-emerald-100 dark:bg-emerald-900/50': selectedLinkedRooms.includes(room.id) }"
                           >
                             <div class="font-medium">{{ room.name }}</div>
                             <div class="text-xs text-gray-500">{{ room.type }}</div>
                           </li>
                         </ul>
                         <div v-else class="text-center text-sm text-gray-500 p-4">
                             Nenhuma sala vinculada a esta agenda.
                         </div>
                       </div>
                    </div>

                  </div>
                </div>

                <!-- Form Actions -->
                <div class="mt-8 flex justify-between items-center">
                  <div class="flex gap-2">
                    <!-- Navigation buttons could go here -->
                  </div>
                  <div class="flex gap-3">
                    <button
                      class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                      @click="goToList"
                    >
                      Cancelar
                    </button>
                    <button
                      class="clinic-btn text-white rounded-md transition-colors shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                      @click="saveSchedule"
                      :disabled="!isFormValid || isSaving"
                    >
                      {{ isSaving ? 'Salvando...' : (editMode ? 'Atualizar' : 'Salvar') }} Agenda
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Success Modal -->
          <div
            class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            v-if="showSuccessModal"
            @click.self="closeSuccessModal"
          >
            <div
              class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn"
            >
              <div class="p-6 text-center">
                <div
                  class="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 mx-auto mb-4"
                >
                  <CheckIcon class="w-8 h-8" />
                </div>
                <h3 class="text-xl font-medium mb-2">{{ editMode ? 'Agenda Atualizada' : 'Agenda Cadastrada' }}</h3>
                <p class="text-gray-600 dark:text-gray-400 mb-6">
                  {{ editMode ? 'A agenda foi atualizada com sucesso.' : 'A agenda foi cadastrada com sucesso no sistema.' }}
                </p>
                <div class="flex justify-center gap-3">
                  <button
                    class="clinic-btn text-white rounded-md transition-colors shadow-sm"
                    @click="closeSuccessModal"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Delete Confirmation Modal -->
          <div
            class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            v-if="showDeleteModal"
            @click.self="showDeleteModal = false"
          >
            <div
              class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fadeIn"
            >
              <div class="p-6">
                <div
                  class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mx-auto mb-4"
                >
                  <AlertTriangleIcon class="w-8 h-8" />
                </div>
                <h3 class="text-xl font-medium mb-2 text-center">Confirmar Exclusão</h3>
                <p class="text-gray-600 dark:text-gray-400 mb-6 text-center">
                  Tem certeza que deseja excluir a agenda <span class="font-medium">{{ scheduleToDelete?.name }}</span>?
                  <br />Esta ação não pode ser desfeita.
                </p>
                <div class="flex justify-end gap-3">
                  <button
                    class="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md transition-colors"
                    @click="showDeleteModal = false"
                  >
                    Cancelar
                  </button>
                  <button
                    class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors shadow-sm"
                    @click="deleteSchedule"
                    :disabled="isDeleting"
                  >
                    {{ isDeleting ? 'Excluindo...' : 'Excluir' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import apiClient from '@/services/authService';
// import { useClinicStore } from '@/stores/settings/clinicStore';
import {
  Plus as PlusIcon,
  ChevronDown as ChevronDownIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Trash as TrashIcon,
  Save as SaveIcon,
  ArrowLeft as ArrowLeftIcon,
  RefreshCw as RefreshCwIcon,
  Search as SearchIcon,
  Loader as LoaderIcon,
  AlertTriangle as AlertTriangleIcon,
  FileText as FileTextIcon,
  Settings as SettingsIcon,
  Clock as ClockIcon,
  ClipboardList as ClipboardListIcon,
  Users as UsersIcon,
  MapPin as MapPinIcon
} from 'lucide-vue-next';

// Inicializa o clinicStore (não usado atualmente)
// const clinicStore = useClinicStore();

// Estado de exibição
const currentView = ref('list');
const editMode = ref(false);
const showSuccessModal = ref(false);
const showDeleteModal = ref(false);
const scheduleToDelete = ref(null);
const searchQuery = ref('');
const filterStatus = ref('');
const filterType = ref('');
const activeTab = ref('basic');
const currentPage = ref(1);
const pageSize = ref(10);
const totalCount = ref(0);
const totalPages = ref(0);
const isLoading = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const errorMessage = ref('');

// Debounce para busca
let searchTimeout = null;

// Abas do formulário
const tabs = [
  { id: 'basic', name: 'Básico', icon: FileTextIcon },
  { id: 'worklist', name: 'Worklist', icon: SettingsIcon },
  { id: 'schedule', name: 'Horários', icon: ClockIcon },
  { id: 'permissions', name: 'Permissões', icon: UsersIcon },
  { id: 'services', name: 'Serviços', icon: ClipboardListIcon },
  { id: 'rooms', name: 'Salas', icon: MapPinIcon }
];

// --- NOVIDADE: Lógica para a Aba de Serviços ---
const allServices = ref([]);
const serviceSearchQuery = ref('');
const selectedAvailable = ref([]);
const selectedLinked = ref([]);

// --- Lógica para a Aba de Salas ---
const allRooms = ref([]);
const roomSearchQuery = ref('');
const selectedAvailableRooms = ref([]);
const selectedLinkedRooms = ref([]);

const availableServices = computed(() => {
    const linkedIds = new Set(currentSchedule.value.service_ids);
    const query = serviceSearchQuery.value.toLowerCase();
    return allServices.value.filter(service => {
        const isNotLinked = !linkedIds.has(service.id);
        const matchesSearch = service.descricao.toLowerCase().includes(query);
        return isNotLinked && matchesSearch;
    });
});

const linkedServices = computed(() => {
    const linkedIds = new Set(currentSchedule.value.service_ids);
    // Preserva a ordem original de 'allServices'
    return allServices.value.filter(service => linkedIds.has(service.id));
});

async function loadAllServices() {
    try {
        const res = await apiClient.get('/api/v1/services/list/');
        allServices.value = res.data.results || [];
    } catch (err) {
        console.error('Erro ao carregar serviços:', err);
        errorMessage.value = 'Não foi possível carregar a lista de serviços.';
    }
}

const availableRooms = computed(() => {
    const linkedIds = new Set(currentSchedule.value.room_ids);
    const query = roomSearchQuery.value.toLowerCase();
    return allRooms.value.filter(room => {
        const isNotLinked = !linkedIds.has(room.id);
        const matchesSearch = room.name.toLowerCase().includes(query) ||
                            room.type.toLowerCase().includes(query);
        return isNotLinked && matchesSearch && room.active;
    });
});

const linkedRooms = computed(() => {
    const linkedIds = new Set(currentSchedule.value.room_ids);
    return allRooms.value.filter(room => linkedIds.has(room.id));
});

async function loadAllRooms() {
    try {
        const res = await apiClient.get('/api/v1/rooms/', { 
            params: { 
                page_size: 100,
                active: true 
            } 
        });
        allRooms.value = res.data.results || [];
    } catch (err) {
        console.error('Erro ao carregar salas:', err);
        errorMessage.value = 'Não foi possível carregar a lista de salas.';
    }
}
// ---------------------------------------------


// Dias da semana
const diasSemana = [
  { nome: 'Segunda' },
  { nome: 'Terça' },
  { nome: 'Quarta' },
  { nome: 'Quinta' },
  { nome: 'Sexta' },
  { nome: 'Sábado' },
  { nome: 'Domingo' }
];

// Modelo vazio de agenda
const emptySchedule = {
  id: null,
  name: '',
  is_consultation: true,
  has_worklist_support: false,
  worklist_config: {
    ip_server: '',
    port: null,
    aetitle: ''
  },
  is_enabled: true,
  allows_manual_registration: true,
  doctor_id: '',
  slots: diasSemana.map((_, index) => ({
    weekday: index,
    is_active: false,
    morning_start: '08:00',
    morning_end: '12:00',
    afternoon_start: '14:00',
    afternoon_end: '18:00',
    interval_minutes: 15
  })),
  permitted_user_ids: [],
  service_ids: [],
  room_ids: [],
  allows_special_slot: false,
  allow_attach_after_report: false,
  allow_attach_after_attendance: false,
  allow_unconfirm: false,
  worklist_generation_mode: 'confirmation' // Adicionado para controlar o modo de geração do worklist
};

// Dados reativos
const schedules = ref([]);
const doctors = ref([]);
const users = ref([]);
const currentSchedule = ref({ ...emptySchedule });

// Computed properties
const isFormValid = computed(() => {
  const schedule = currentSchedule.value;
  const isValidString = (value) => value && typeof value === 'string' && value.trim() !== '';
  
  let isValid = (
    isValidString(schedule.name) &&
    schedule.doctor_id !== ''
  );
  
  // Validação específica para worklist
  if (schedule.has_worklist_support) {
    const worklist = schedule.worklist_config;
    isValid = isValid && (
      isValidString(worklist.ip_server) &&
      worklist.port !== null &&
      worklist.port > 0 &&
      isValidString(worklist.aetitle)
    );
  }
  
  return isValid;
});

// Computed property para agendas sem serviços
const schedulesWithoutServices = computed(() => {
  return schedules.value.filter(schedule => 
    !schedule.services || schedule.services.length === 0
  );
});

// Métodos de API
async function loadSchedules(page = 1) {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const params = {
      page,
      page_size: pageSize.value
    };
    
    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim();
    }
    
    if (filterStatus.value) {
      params.is_enabled = filterStatus.value;
    }
    
    if (filterType.value) {
      params.is_consultation = filterType.value;
    }
    
    const response = await apiClient.get('/api/v1/schedules/list/', { params });
    
    schedules.value = response.data.results;
    totalCount.value = response.data.count;
    totalPages.value = response.data.total_pages;
    currentPage.value = page;
    
  } catch (error) {
    console.error('Erro ao carregar agendas:', error);
    errorMessage.value = 'Erro ao carregar agendas. Tente novamente.';
    
    if (error.response?.status === 401) {
      errorMessage.value = 'Sessão expirada. Faça login novamente.';
    }
  } finally {
    isLoading.value = false;
  }
}

async function loadDoctors() {
  try {
    const response = await apiClient.get('/api/v1/schedules/doctors/list/');
    doctors.value = response.data;
  } catch (error) {
    console.error('Erro ao carregar médicos:', error);
  }
}

async function loadUsers() {
  try {
    const response = await apiClient.get('/api/v1/auth/users/');
    users.value = response.data;
  } catch (error) {
    console.error('Erro ao carregar usuários:', error);
  }
}

async function saveSchedule() {
  if (!isFormValid.value || isSaving.value) return;
  isSaving.value = true;
  errorMessage.value = '';

  const scheduleData = JSON.parse(JSON.stringify(currentSchedule.value));
  scheduleData.service_ids = currentSchedule.value.service_ids;
  scheduleData.room_ids = currentSchedule.value.room_ids;
  if (scheduleData.has_worklist_support) {
    scheduleData.worklist_config.port = Number(scheduleData.worklist_config.port);
  } else {
    delete scheduleData.worklist_config;
  }

  delete scheduleData.id;
  delete scheduleData.created_at;
  delete scheduleData.updated_at;
  delete scheduleData.created_by;
  delete scheduleData.updated_by;
  delete scheduleData.doctor;
  delete scheduleData.permitted_users;

  try {
    if (editMode.value) {
      await apiClient.put(
        `/api/v1/schedules/update/${currentSchedule.value.id}/`,
        scheduleData
      );
    } else {
      await apiClient.post(
        '/api/v1/schedules/add/',
        scheduleData
      );
    }
    showSuccessModal.value = true;
    await loadSchedules(currentPage.value);
  } catch (err) {
    console.error('Erro ao salvar agenda:', err);
    const status = err.response?.status;
    if (status === 401) {
      errorMessage.value = 'Sessão expirada. Faça login novamente.';
    } else if (status === 400) {
      errorMessage.value = 'Dados inválidos. Verifique os campos obrigatórios.';
    } else {
      errorMessage.value = 'Erro ao salvar agenda. Tente novamente.';
    }
  } finally {
    isSaving.value = false;
  }
}

async function deleteSchedule() {
  if (!scheduleToDelete.value || isDeleting.value) return;
  isDeleting.value = true;

  try {
    await apiClient.delete(`/api/v1/schedules/delete/${scheduleToDelete.value.id}/`);
    showDeleteModal.value = false;
    scheduleToDelete.value = null;
    await loadSchedules(currentPage.value);
  } catch (error) {
    console.error('Erro ao excluir agenda:', error);
    errorMessage.value = error.response?.status === 401
      ? 'Sessão expirada. Faça login novamente.'
      : 'Erro ao excluir agenda. Tente novamente.';
  } finally {
    isDeleting.value = false;
  }
}

// Métodos de controle da UI
function openNewScheduleForm() {
  currentSchedule.value = JSON.parse(JSON.stringify(emptySchedule));
  editMode.value = false;
  activeTab.value = 'basic';
  currentView.value = 'form';
  errorMessage.value = '';
  // Limpa seleções da aba de serviços
  selectedAvailable.value = [];
  selectedLinked.value = [];
  serviceSearchQuery.value = '';
  // Limpa seleções da aba de salas
  selectedAvailableRooms.value = [];
  selectedLinkedRooms.value = [];
  roomSearchQuery.value = '';
}

async function editSchedule(schedule) {
  errorMessage.value = '';
  isLoading.value = true;

  // Limpa seleções anteriores
  selectedAvailable.value = [];
  selectedLinked.value = [];
  serviceSearchQuery.value = '';
  selectedAvailableRooms.value = [];
  selectedLinkedRooms.value = [];
  roomSearchQuery.value = '';

  try {
    const response = await apiClient.get(`/api/v1/schedules/${schedule.id}/`);
    const full = response.data;

    const scheduleData = {
      id: full.id,
      name: full.name,
      is_consultation: full.is_consultation,
      is_enabled: full.is_enabled,
      allows_manual_registration: full.allows_manual_registration,
      allows_special_slot: full.allows_special_slot,
      allow_attach_after_report: full.allow_attach_after_report === true,
      allow_attach_after_attendance: full.allow_attach_after_attendance === true,
      allow_unconfirm : full.allow_unconfirm === true,
      doctor_id: full.doctor?.id || '',
      has_worklist_support: full.has_worklist_support,
      worklist_config: full.has_worklist_support && full.worklist_config
        ? { ...full.worklist_config }
        : { ip_server: '', port: null, aetitle: '' },
      slots: diasSemana.map((_, weekday) => {
        const slot = full.slots.find(s => s.weekday === weekday);
        return slot ? { ...slot } : {
          weekday, is_active: false, morning_start: '08:00', morning_end: '12:00',
          afternoon_start: '14:00', afternoon_end: '18:00', interval_minutes: 15,
        };
      }),
      permitted_user_ids: full.permitted_users.map(u => u.id),
      service_ids: full.services.map(s => s.id),
      room_ids: full.rooms ? full.rooms.map(r => r.id) : [],
      worklist_generation_mode: full.worklist_generation_mode || 'confirmation' // Adicionado para carregar o modo de geração
    };

    currentSchedule.value = scheduleData;
    editMode.value = true;
    activeTab.value = 'basic';
    currentView.value = 'form';

  } catch (err) {
    console.error('Erro ao carregar detalhes da agenda:', err);
    errorMessage.value = err.response?.status === 404
      ? 'Agenda não encontrada.'
      : 'Erro ao carregar detalhes. Tente novamente.';
  } finally {
    isLoading.value = false;
  }
}

function goToList() {
  currentView.value = 'list';
  errorMessage.value = '';
}

function confirmDeleteSchedule(schedule) {
  scheduleToDelete.value = schedule;
  showDeleteModal.value = true;
}

function resetFilters() {
  searchQuery.value = '';
  filterStatus.value = '';
  filterType.value = '';
  loadSchedules(1);
}

function closeSuccessModal() {
  showSuccessModal.value = false;
  currentView.value = 'list';
}

function debouncedSearch() {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => loadSchedules(1), 500);
}

// --- NOVIDADE: Métodos para a Aba de Serviços ---
function toggleSelection(type, serviceId) {
    const selectionArray = type === 'available' ? selectedAvailable : selectedLinked;
    const index = selectionArray.value.indexOf(serviceId);
    if (index > -1) {
        selectionArray.value.splice(index, 1);
    } else {
        selectionArray.value.push(serviceId);
    }
}

function moveSelectedToRight() {
    currentSchedule.value.service_ids.push(...selectedAvailable.value);
    selectedAvailable.value = [];
}

function moveAllToRight() {
    const idsToMove = availableServices.value.map(s => s.id);
    currentSchedule.value.service_ids.push(...idsToMove);
}

function moveSelectedToLeft() {
    currentSchedule.value.service_ids = currentSchedule.value.service_ids.filter(
        id => !selectedLinked.value.includes(id)
    );
    selectedLinked.value = [];
}

function moveAllToLeft() {
    currentSchedule.value.service_ids = [];
    selectedLinked.value = [];
}

// --- Métodos para a Aba de Salas ---
function toggleRoomSelection(type, roomId) {
    const selectionArray = type === 'available' ? selectedAvailableRooms : selectedLinkedRooms;
    const index = selectionArray.value.indexOf(roomId);
    if (index > -1) {
        selectionArray.value.splice(index, 1);
    } else {
        selectionArray.value.push(roomId);
    }
}

function moveSelectedRoomsToRight() {
    currentSchedule.value.room_ids.push(...selectedAvailableRooms.value);
    selectedAvailableRooms.value = [];
}

function moveAllRoomsToRight() {
    const idsToMove = availableRooms.value.map(r => r.id);
    currentSchedule.value.room_ids.push(...idsToMove);
}

function moveSelectedRoomsToLeft() {
    currentSchedule.value.room_ids = currentSchedule.value.room_ids.filter(
        id => !selectedLinkedRooms.value.includes(id)
    );
    selectedLinkedRooms.value = [];
}

function moveAllRoomsToLeft() {
    currentSchedule.value.room_ids = [];
    selectedLinkedRooms.value = [];
}
// ------------------------------------------------

// Utilitários
function getUserDisplayName(user) {
  if (user.first_name || user.last_name) {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }
  return user.username;
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    loadSchedules(1),
    loadDoctors(),
    loadUsers(),
    loadAllServices(),
    loadAllRooms()
  ]);
  currentView.value = 'list';
});
</script>

<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.clinic-btn {
  background-color: var(--clinic-btn-bg);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
}

.clinic-btn:hover:not(:disabled) {
  background-color: var(--clinic-btn-bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.clinic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.input-font-color {
  color: var(--clinic-font-color);
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>